import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isLoggedIn, getUser, verifySession, getStudents, getTrainingDays, getTrainingDetails, getStudentExercises, getTrainingFull, getStudentTemplates, updateTraining, createTraining, moveTraining, updateExercise, createExercise, getCurrentSubscription, getStudentStats, getExerciseProgress, getOverviewHeatmap, getCoachAlerts, getCoachTemplates, sendTemplateToStudent, scheduleProgramming, duplicateTrainingMultiple, getStudentScores } from "../services/authService";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend } from "chart.js";
import { Bar, Line } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend);

const MONTH_NAMES = [
	"Janvier", "Fevrier", "Mars", "Avril", "Mai", "Juin",
	"Juillet", "Aout", "Septembre", "Octobre", "Novembre", "Decembre"
];

const DAY_NAMES = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

// Hardcoded muscle labels (same order as in the mobile app)
const MUSCLE_LABELS = [
	"Pectoraux",
	"Epaules",
	"Dorsaux",
	"Trapezes",
	"Biceps",
	"Triceps",
	"Avant-bras",
	"Cou",
	"Fessiers",
	"Quadriceps",
	"Ischio-jambiers",
	"Mollets",
	"Abdominaux",
	"Lombaires",
	"Adducteurs",
	"Abducteurs"
];

// Helper function to get muscle names from muscles array
const getMuscleNames = (muscles) => {
	if (!muscles || muscles.length === 0) return [];
	return muscles
		.map((value, index) => (value === 1 ? MUSCLE_LABELS[index] : null))
		.filter(Boolean);
};

function CoachMode() {
	const navigate = useNavigate();
	const [user, setUser] = useState(null);
	const [loading, setLoading] = useState(true);
	const [students, setStudents] = useState([]);
	const [selectedStudent, setSelectedStudent] = useState(null);

	// Calendar state
	const [currentDate, setCurrentDate] = useState(new Date());
	const [trainingDays, setTrainingDays] = useState([]);
	const [loadingCalendar, setLoadingCalendar] = useState(false);
	const [selectedDay, setSelectedDay] = useState(null);
	const [trainingDetails, setTrainingDetails] = useState([]);
	const [loadingDetails, setLoadingDetails] = useState(false);

	// Exercises state
	const [exercises, setExercises] = useState([]);
	const [loadingExercises, setLoadingExercises] = useState(false);
	const [selectedExercise, setSelectedExercise] = useState(null);
	const [exerciseSearch, setExerciseSearch] = useState("");

	// Editor state
	const [editorTraining, setEditorTraining] = useState(null);
	const [loadingEditor, setLoadingEditor] = useState(false);
	const [editorMode, setEditorMode] = useState(null); // 'edit' or 'create'
	const [savingTraining, setSavingTraining] = useState(false);

	// Templates state
	const [templates, setTemplates] = useState([]);
	const [loadingTemplates, setLoadingTemplates] = useState(false);

	// Drag and drop state
	const [draggedExercise, setDraggedExercise] = useState(null);

	// Date picker modal state
	const [showDatePicker, setShowDatePicker] = useState(false);
	const [selectedCalendarDate, setSelectedCalendarDate] = useState(new Date());
	const [datePickerAction, setDatePickerAction] = useState(null); // 'addToCalendar', 'move', 'duplicate'
	const [selectedOverviewTraining, setSelectedOverviewTraining] = useState(null);

	// Template name modal state
	const [showTemplateNameModal, setShowTemplateNameModal] = useState(false);
	const [templateListTab, setTemplateListTab] = useState("student");
	const [templateSaveTarget, setTemplateSaveTarget] = useState("student");
	const [templateName, setTemplateName] = useState("");

	// Exercise edit state
	const [exerciseEditMode, setExerciseEditMode] = useState(false);
	const [editingExerciseName, setEditingExerciseName] = useState("");
	const [editingExerciseDescription, setEditingExerciseDescription] = useState("");
	const [editingExerciseMuscles, setEditingExerciseMuscles] = useState([]);
	const [savingExercise, setSavingExercise] = useState(false);

	// Create exercise modal state
	const [showCreateExerciseModal, setShowCreateExerciseModal] = useState(false);
	const [newExerciseName, setNewExerciseName] = useState("");
	const [newExerciseDescription, setNewExerciseDescription] = useState("");
	const [newExerciseMuscles, setNewExerciseMuscles] = useState(MUSCLE_LABELS.map(() => 0));
	const [creatingExercise, setCreatingExercise] = useState(false);

	// Training comment modal state
	const [showCommentModal, setShowCommentModal] = useState(false);
	const [editingComment, setEditingComment] = useState("");

	// Subscription state
	const [subscriptionBlocked, setSubscriptionBlocked] = useState(false);

	// Track if training has been modified
	const [trainingModified, setTrainingModified] = useState(false);

	// Tab navigation
	const [activeTab, setActiveTab] = useState("editeur");

	// Multi-date duplication
	const [multiSelectMode, setMultiSelectMode] = useState(false);
	const [selectedDates, setSelectedDates] = useState([]);
	const [duplicateSourceTraining, setDuplicateSourceTraining] = useState(null);

	// Planification
	const [showPlanification, setShowPlanification] = useState(false);
	const [planSelectedTemplate, setPlanSelectedTemplate] = useState(null);
	const [planTemplateSource, setPlanTemplateSource] = useState("coach");
	const [coachTemplates, setCoachTemplates] = useState([]);
	const [planDays, setPlanDays] = useState([false, false, false, false, false, false, false]);
	const [planWeeks, setPlanWeeks] = useState(4);
	const [planStartDate, setPlanStartDate] = useState(new Date().toISOString().split('T')[0]);
	const [planSaveAsTemplate, setPlanSaveAsTemplate] = useState(false);
	const [planLoading, setPlanLoading] = useState(false);

	// Is the selected student actually the coach themselves?
	const isSelf = selectedStudent && user && selectedStudent.id === user.id;

	// Stats tab
	const [statsData, setStatsData] = useState(null);
	const [statsPeriod, setStatsPeriod] = useState(30);
	const [statsLoading, setStatsLoading] = useState(false);
	const [exerciseProgressData, setExerciseProgressData] = useState(null);
	const [selectedProgressExercise, setSelectedProgressExercise] = useState(null);
	const [exerciseProgressPeriod, setExerciseProgressPeriod] = useState(90);
	const [exerciseProgressData2, setExerciseProgressData2] = useState(null);
	const [selectedProgressExercise2, setSelectedProgressExercise2] = useState(null);
	const [exerciseProgressPeriod2, setExerciseProgressPeriod2] = useState(90);

	// Overview tab
	const [heatmapData, setHeatmapData] = useState(null);
	const [studentScores, setStudentScores] = useState(null);
	const [scoresSortBy, setScoresSortBy] = useState("regularity");
	const [alertsData, setAlertsData] = useState(null);
	const [overviewLoading, setOverviewLoading] = useState(false);
	const [selectedHeatmapDay, setSelectedHeatmapDay] = useState(null);
	const [heatmapWeekOffset, setHeatmapWeekOffset] = useState(0);
	const [overviewFilterStudent, setOverviewFilterStudent] = useState(null);
	const [overviewPeriod, setOverviewPeriod] = useState(90);
	const [trainingDetailModal, setTrainingDetailModal] = useState(null); // { studentName, training, previousTraining }
	const [trainingDetailLoading, setTrainingDetailLoading] = useState(false);

	useEffect(() => {
		const checkSession = async () => {
			if (!isLoggedIn()) {
				navigate("/login");
				return;
			}

			const isValid = await verifySession();
			if (!isValid) {
				navigate("/login");
				return;
			}

			setUser(getUser());

			// Check subscription — dashboard requires Pro tier (idAbonnementType 4 or 6)
			const subResult = await getCurrentSubscription();
			if (subResult.success && subResult.subscription) {
				const proTierIds = [4, 5, 6]; // Pro Mensuel, Testeur, Pro Annuel
				if (!proTierIds.includes(subResult.subscription.idAbonnementType)) {
					setSubscriptionBlocked(true);
					setLoading(false);
					return;
				}
			} else {
				setSubscriptionBlocked(true);
				setLoading(false);
				return;
			}

			// Fetch students
			const result = await getStudents();
			if (result.success) {
				setStudents(result.students);
			}

			setLoading(false);
		};

		checkSession();
	}, [navigate]);

	// Fetch training days when student or month changes
	useEffect(() => {
		const fetchTrainingDays = async () => {
			if (!selectedStudent) {
				setTrainingDays([]);
				return;
			}

			setLoadingCalendar(true);
			setSelectedDay(null);
			setTrainingDetails([]);
			const year = currentDate.getFullYear();
			const month = currentDate.getMonth() + 1; // JavaScript months are 0-indexed

			const result = await getTrainingDays(selectedStudent.id, year, month);
			if (result.success) {
				setTrainingDays(result.trainingDays);
			} else {
				setTrainingDays([]);
			}
			setLoadingCalendar(false);
		};

		fetchTrainingDays();
	}, [selectedStudent, currentDate]);

	// Fetch training details when a day is selected
	useEffect(() => {
		const fetchTrainingDetails = async () => {
			if (!selectedStudent || !selectedDay) {
				setTrainingDetails([]);
				return;
			}

			setLoadingDetails(true);
			const year = currentDate.getFullYear();
			const month = currentDate.getMonth() + 1;

			const result = await getTrainingDetails(selectedStudent.id, year, month, selectedDay);
			if (result.success) {
				setTrainingDetails(result.trainings);
			} else {
				setTrainingDetails([]);
			}
			setLoadingDetails(false);
		};

		fetchTrainingDetails();
	}, [selectedStudent, selectedDay, currentDate]);

	// Fetch exercises when student changes
	useEffect(() => {
		const fetchExercises = async () => {
			if (!selectedStudent) {
				setExercises([]);
				return;
			}

			setLoadingExercises(true);
			const result = await getStudentExercises(selectedStudent.id);
			if (result.success) {
				setExercises(result.exercises);
			} else {
				setExercises([]);
			}
			setLoadingExercises(false);
		};

		fetchExercises();
	}, [selectedStudent]);

	// Fetch templates when student changes
	useEffect(() => {
		const fetchTemplates = async () => {
			if (!selectedStudent) {
				setTemplates([]);
				setCoachTemplates([]);
				return;
			}

			setLoadingTemplates(true);
			const [studentRes, coachRes] = await Promise.all([
				getStudentTemplates(selectedStudent.id),
				getCoachTemplates()
			]);
			setTemplates(studentRes.success ? studentRes.templates : []);
			setCoachTemplates(coachRes.success ? (coachRes.data?.templates || []) : []);
			setLoadingTemplates(false);
		};

		fetchTemplates();
	}, [selectedStudent]);

	const handleStudentChange = (e) => {
		const studentId = parseInt(e.target.value);
		const student = students.find((s) => s.id === studentId)
			|| (user && user.id === studentId ? { id: user.id, prenom: user.prenom, nom: user.nom } : null);
		setSelectedStudent(student || null);
		setSelectedDay(null);
		setTrainingDetails([]);
		// When selecting self, force template tabs to coach
		if (user && studentId === user.id) {
			setTemplateListTab("coach");
			setTemplateSaveTarget("coach");
			setPlanTemplateSource("coach");
		}
		// Cancel multi-date mode when changing student
		if (multiSelectMode) cancelMultiDateDuplicate();
	};

	const handleDayClick = (day) => {
		if (multiSelectMode) {
			const year = currentDate.getFullYear();
			const month = currentDate.getMonth();
			const dateTs = new Date(year, month, day).getTime();
			toggleDateSelection(dateTs);
		} else {
			setSelectedDay(day);
		}
	};

	const handleExerciseClick = (exercise) => {
		setSelectedExercise(exercise);
	};

	const closeExerciseModal = () => {
		setSelectedExercise(null);
		setExerciseEditMode(false);
	};

	const startEditExercise = () => {
		if (!selectedExercise) return;
		setEditingExerciseName(selectedExercise.nomExercice || "");
		setEditingExerciseDescription(selectedExercise.comExercice || "");
		// Ensure muscles array has 16 elements (0 or 1 for each muscle)
		const muscles = selectedExercise.muscles || [];
		const normalizedMuscles = MUSCLE_LABELS.map((_, index) => muscles[index] || 0);
		setEditingExerciseMuscles(normalizedMuscles);
		setExerciseEditMode(true);
	};

	const cancelEditExercise = () => {
		setExerciseEditMode(false);
	};

	const toggleMuscle = (index) => {
		const newMuscles = [...editingExerciseMuscles];
		newMuscles[index] = newMuscles[index] === 1 ? 0 : 1;
		setEditingExerciseMuscles(newMuscles);
	};

	const saveExerciseChanges = async () => {
		if (!selectedExercise || !selectedStudent) return;

		setSavingExercise(true);

		const result = await updateExercise(
			selectedStudent.id,
			selectedExercise.id,
			editingExerciseName,
			editingExerciseDescription,
			editingExerciseMuscles
		);

		setSavingExercise(false);

		if (result.success) {
			// Update the exercise in local state
			const updatedExercises = exercises.map((ex) => {
				if (ex.id === selectedExercise.id) {
					return {
						...ex,
						nomExercice: editingExerciseName,
						comExercice: editingExerciseDescription,
						muscles: editingExerciseMuscles
					};
				}
				return ex;
			});
			setExercises(updatedExercises);

			// Update selectedExercise to reflect changes
			setSelectedExercise({
				...selectedExercise,
				nomExercice: editingExerciseName,
				comExercice: editingExerciseDescription,
				muscles: editingExerciseMuscles
			});

			setExerciseEditMode(false);
			alert("Exercice modifié avec succès");
		} else {
			alert("Erreur lors de la modification: " + result.error);
		}
	};

	const openCreateExerciseModal = () => {
		setNewExerciseName("");
		setNewExerciseDescription("");
		setNewExerciseMuscles(MUSCLE_LABELS.map(() => 0));
		setShowCreateExerciseModal(true);
	};

	const closeCreateExerciseModal = () => {
		setShowCreateExerciseModal(false);
	};

	const toggleNewExerciseMuscle = (index) => {
		const newMuscles = [...newExerciseMuscles];
		newMuscles[index] = newMuscles[index] === 1 ? 0 : 1;
		setNewExerciseMuscles(newMuscles);
	};

	const openCommentModal = () => {
		setEditingComment(editorTraining?.commentaire || "");
		setShowCommentModal(true);
	};

	const closeCommentModal = () => {
		setShowCommentModal(false);
	};

	const saveComment = () => {
		if (!editorTraining) return;
		setEditorTraining({
			...editorTraining,
			commentaire: editingComment
		});
		setTrainingModified(true);
		setShowCommentModal(false);
	};

	const handleCreateExercise = async () => {
		if (!selectedStudent || !newExerciseName.trim()) return;

		setCreatingExercise(true);

		const result = await createExercise(
			selectedStudent.id,
			newExerciseName.trim(),
			newExerciseDescription,
			newExerciseMuscles
		);

		setCreatingExercise(false);

		if (result.success) {
			// Add the new exercise to local state
			const newExercise = {
				id: result.id,
				nomExercice: newExerciseName.trim(),
				comExercice: newExerciseDescription,
				muscles: newExerciseMuscles
			};
			setExercises([...exercises, newExercise].sort((a, b) =>
				a.nomExercice.localeCompare(b.nomExercice)
			));
			setShowCreateExerciseModal(false);
			alert("Exercice créé avec succès");
		} else {
			alert(result.error);
		}
	};

	const handleAddToSession = () => {
		const now = Date.now();
		const newExercise = {
			id: -1, // New exercise, not yet saved
			localId: now, // Local identifier like in the mobile app
			nom: selectedExercise.nomExercice,
			idExercice: selectedExercise.id,
			supprime: 0,
			series: [
				{ id: -1, reps: 0, poids: 0, rpe: 0 },
				{ id: -1, reps: 0, poids: 0, rpe: 0 }
			]
		};

		if (!editorTraining) {
			// Create a new training if none is open
			setEditorTraining({
				id: null,
				nom: "Nouvelle seance",
				date: Date.now(),
				estTemplate: false,
				exercices: [newExercise]
			});
			setEditorMode('create');
		} else {
			// Add to existing training
			const updated = { ...editorTraining };
			updated.exercices.push(newExercise);
			setEditorTraining({ ...updated });
		}
		setTrainingModified(true);
		closeExerciseModal();
	};

	const openInEditor = async (trainingId, isTemplate = false) => {
		if (!selectedStudent) return;

		if (editorTraining && trainingModified) {
			const confirmed = window.confirm("Une séance est en cours d'édition, êtes vous sûr de vouloir continuer ?");
			if (!confirmed) return;
		}

		setLoadingEditor(true);
		const result = await getTrainingFull(selectedStudent.id, trainingId);
		if (result.success) {
			// Deep copy to make it editable
			const training = JSON.parse(JSON.stringify(result.training));

			// Add supprime and localId fields to each exercise
			training.exercices = training.exercices.map((ex) => ({
				...ex,
				supprime: 0,
				localId: ex.id // Use db id as localId for existing exercises
			}));

			// Mark if this is a template
			training.estTemplate = isTemplate;

			setEditorTraining(training);
			// Use 'editTemplate' for existing templates, 'edit' for trainings
			setEditorMode(isTemplate ? 'editTemplate' : 'edit');
			setTrainingModified(false);
		}
		setLoadingEditor(false);
	};

	const createNewTraining = () => {
		if (editorTraining && trainingModified) {
			const confirmed = window.confirm("Une séance est en cours d'édition, êtes vous sûr de vouloir continuer ?");
			if (!confirmed) return;
		}

		setEditorTraining({
			id: null,
			nom: "Nouvelle seance",
			date: Date.now(),
			estTemplate: false,
			exercices: []
		});
		setEditorMode('create');
		setTrainingModified(false);
	};

	const removeExercice = (exerciceIndex) => {
		if (!editorTraining) return;
		const updated = { ...editorTraining };

		// Get the actual exercise (accounting for hidden deleted ones)
		const visibleExercises = updated.exercices.filter((ex) => ex.supprime !== 1);
		const exerciseToRemove = visibleExercises[exerciceIndex];

		// Find its real index in the full array
		const realIndex = updated.exercices.findIndex(
			(ex) => (ex.localId && ex.localId === exerciseToRemove.localId) || (ex.id > 0 && ex.id === exerciseToRemove.id)
		);

		if (realIndex !== -1) {
			// Mark as deleted instead of removing
			updated.exercices[realIndex].supprime = 1;
			setEditorTraining({ ...updated });
			setTrainingModified(true);
		}
	};

	const handleDragStart = (e, visibleIndex) => {
		setDraggedExercise(visibleIndex);
		e.dataTransfer.effectAllowed = 'move';
	};

	const handleDragOver = (e, visibleIndex) => {
		e.preventDefault();
		if (draggedExercise === null || draggedExercise === visibleIndex) return;

		const updated = { ...editorTraining };
		// Work with visible exercises only
		const visibleExercises = updated.exercices.filter((ex) => ex.supprime !== 1);
		const deletedExercises = updated.exercices.filter((ex) => ex.supprime === 1);

		// Reorder visible exercises
		const draggedItem = visibleExercises[draggedExercise];
		visibleExercises.splice(draggedExercise, 1);
		visibleExercises.splice(visibleIndex, 0, draggedItem);

		// Combine back with deleted exercises at the end
		updated.exercices = [...visibleExercises, ...deletedExercises];
		setEditorTraining({ ...updated });
		setDraggedExercise(visibleIndex);
		setTrainingModified(true);
	};

	const handleDragEnd = () => {
		setDraggedExercise(null);
	};

	const handleDeleteTraining = () => {
		const confirmed = window.confirm("Êtes-vous sûr de vouloir supprimer cet entraînement ?");
		if (confirmed) {
			// TODO: Implement actual deletion
			console.log("Deleting training:", editorTraining.id);
			setEditorTraining(null);
			setEditorMode(null);
		}
	};

	const handleDeleteTemplate = () => {
		const confirmed = window.confirm("Êtes-vous sûr de vouloir supprimer ce template ?");
		if (confirmed) {
			// TODO: Implement actual deletion
			console.log("Deleting template:", editorTraining.id);
			setEditorTraining(null);
			setEditorMode(null);
		}
	};

	const handleSaveTemplate = async () => {
		if (!editorTraining || !selectedStudent) return;

		setSavingTraining(true);

		// Transform data to API format with IDs and supprime status
		let ordre = 0;
		const exercicesForApi = editorTraining.exercices.map((ex) => {
			const exercice = {
				Id: ex.id, // Database ID (-1 for new)
				LocalId: ex.localId || Date.now(), // Local identifier (fallback to timestamp if missing)
				IdExercice: ex.idExercice, // Referentiel exercise ID
				Supprime: ex.supprime || 0,
				Ordre: ex.supprime === 1 ? -1 : ordre++,
				ListSerie: ex.series.map((s) => ({
					reps: s.reps,
					poids: s.poids,
					rpe: s.rpe || 0
				}))
			};
			return exercice;
		});

		const result = await updateTraining(
			selectedStudent.id,
			editorTraining.id,
			editorTraining.nom,
			editorTraining.date,
			true, // EstTemplate = true
			exercicesForApi,
			editorTraining.commentaire || ""
		);

		setSavingTraining(false);

		if (result.success) {
			// Refresh templates list
			const templatesResult = await getStudentTemplates(selectedStudent.id);
			if (templatesResult.success) {
				setTemplates(templatesResult.templates);
			}
			// Clear editor after save
			setEditorTraining(null);
			setEditorMode(null);
			setTrainingModified(false);
			alert("Template enregistré avec succès");
		} else {
			alert("Erreur lors de l'enregistrement: " + result.error);
		}
	};

	const handleSaveTraining = async () => {
		if (!editorTraining || !selectedStudent) return;

		setSavingTraining(true);

		// Transform data to API format with IDs and supprime status
		// Calculate ordre based on visible exercises order
		let ordre = 0;
		const exercicesForApi = editorTraining.exercices.map((ex) => {
			const exercice = {
				Id: ex.id, // Database ID (-1 for new)
				LocalId: ex.localId || Date.now(), // Local identifier (fallback to timestamp if missing)
				IdExercice: ex.idExercice, // Referentiel exercise ID
				Supprime: ex.supprime || 0,
				Ordre: ex.supprime === 1 ? -1 : ordre++, // Only increment ordre for non-deleted
				ListSerie: ex.series.map((s) => ({
					reps: s.reps,
					poids: s.poids,
					rpe: s.rpe || 0
				}))
			};
			return exercice;
		});

		const result = await updateTraining(
			selectedStudent.id,
			editorTraining.id,
			editorTraining.nom,
			editorTraining.date,
			editorTraining.estTemplate || false,
			exercicesForApi,
			editorTraining.commentaire || ""
		);

		setSavingTraining(false);

		if (result.success) {
			// Refresh training details if we're viewing the same day
			if (selectedDay) {
				const year = currentDate.getFullYear();
				const month = currentDate.getMonth() + 1;
				const detailsResult = await getTrainingDetails(selectedStudent.id, year, month, selectedDay);
				if (detailsResult.success) {
					setTrainingDetails(detailsResult.trainings);
				}
			}
			// Clear editor after save
			setEditorTraining(null);
			setEditorMode(null);
			setTrainingModified(false);
			alert("Entraînement enregistré avec succès");
		} else {
			alert("Erreur lors de l'enregistrement: " + result.error);
		}
	};

	const openDatePickerModal = (action, overviewTraining = null) => {
		setSelectedCalendarDate(new Date());
		setDatePickerAction(action);
		setSelectedOverviewTraining(overviewTraining);
		setShowDatePicker(true);
	};

	const closeDatePickerModal = () => {
		setShowDatePicker(false);
		setDatePickerAction(null);
		setSelectedOverviewTraining(null);
	};

	const handleAddToCalendar = async () => {
		if (!editorTraining || !selectedStudent) return;

		setSavingTraining(true);
		setShowDatePicker(false);

		// Convert selected date to timestamp
		const dateTimestamp = selectedCalendarDate.getTime();

		// Transform data to API format - filter out deleted exercises
		let ordre = 0;
		const exercicesForApi = editorTraining.exercices
			.filter((ex) => ex.supprime !== 1)
			.map((ex) => ({
				Id: -1, // New exercise
				LocalId: ex.localId || Date.now(),
				IdExercice: ex.idExercice,
				Supprime: 0,
				Ordre: ordre++,
				ListSerie: ex.series.map((s) => ({
					reps: s.reps,
					poids: s.poids,
					rpe: s.rpe || 0
				}))
			}));

		const result = await createTraining(
			selectedStudent.id,
			editorTraining.nom,
			dateTimestamp,
			false, // EstTemplate = false
			exercicesForApi
		);

		setSavingTraining(false);

		if (result.success) {
			// Check if the selected date is in the current month view
			const selectedYear = selectedCalendarDate.getFullYear();
			const selectedMonth = selectedCalendarDate.getMonth();
			const currentYear = currentDate.getFullYear();
			const currentMonth = currentDate.getMonth();

			if (selectedYear === currentYear && selectedMonth === currentMonth) {
				// Refresh training days
				const daysResult = await getTrainingDays(selectedStudent.id, selectedYear, selectedMonth + 1);
				if (daysResult.success) {
					setTrainingDays(daysResult.trainingDays);
				}
			}

			setEditorTraining(null);
			setEditorMode(null);
			setTrainingModified(false);
			alert("Entraînement ajouté au calendrier avec succès");
		} else {
			alert("Erreur lors de l'enregistrement: " + result.error);
		}
	};

	const handleMoveTraining = async () => {
		if (!selectedOverviewTraining || !selectedStudent) return;

		setSavingTraining(true);
		setShowDatePicker(false);

		const dateTimestamp = selectedCalendarDate.getTime();

		const result = await moveTraining(
			selectedStudent.id,
			selectedOverviewTraining.id,
			dateTimestamp
		);

		setSavingTraining(false);
		setSelectedOverviewTraining(null);
		setDatePickerAction(null);

		if (result.success) {
			// Refresh training days for the current month view
			const daysResult = await getTrainingDays(selectedStudent.id, currentDate.getFullYear(), currentDate.getMonth() + 1);
			if (daysResult.success) {
				setTrainingDays(daysResult.trainingDays);
			}

			// Clear selected day and details since the training moved
			setSelectedDay(null);
			setTrainingDetails([]);

			alert("Entraînement déplacé avec succès");
		} else {
			alert("Erreur lors du déplacement: " + result.error);
		}
	};

	const handleDuplicateTraining = async () => {
		if (!selectedOverviewTraining || !selectedStudent) return;

		setSavingTraining(true);
		setShowDatePicker(false);

		// Fetch full training data
		const fullResult = await getTrainingFull(selectedStudent.id, selectedOverviewTraining.id);
		if (!fullResult.success) {
			setSavingTraining(false);
			alert("Erreur lors de la récupération de l'entraînement");
			return;
		}

		const training = fullResult.training;
		const dateTimestamp = selectedCalendarDate.getTime();

		// Create new exercises with new LocalIds (similar to duplicateSeance in the app)
		const baseTime = Date.now();
		const exercicesForApi = training.exercices.map((ex, index) => ({
			Id: -1, // New exercise
			LocalId: baseTime + index, // New unique LocalId for each exercise
			IdExercice: ex.idExercice, // Keep the referentiel exercise ID
			Supprime: 0,
			Ordre: index,
			ListSerie: ex.series.map((s) => ({
				reps: 0, // Reset reps to 0 like in the app
				poids: s.poids,
				rpe: 0
			}))
		}));

		const result = await createTraining(
			selectedStudent.id,
			training.nom,
			dateTimestamp,
			false, // EstTemplate = false
			exercicesForApi
		);

		setSavingTraining(false);
		setSelectedOverviewTraining(null);
		setDatePickerAction(null);

		if (result.success) {
			// Check if the selected date is in the current month view
			const selectedYear = selectedCalendarDate.getFullYear();
			const selectedMonth = selectedCalendarDate.getMonth();
			const currentYear = currentDate.getFullYear();
			const currentMonth = currentDate.getMonth();

			if (selectedYear === currentYear && selectedMonth === currentMonth) {
				// Refresh training days
				const daysResult = await getTrainingDays(selectedStudent.id, selectedYear, selectedMonth + 1);
				if (daysResult.success) {
					setTrainingDays(daysResult.trainingDays);
				}
			}

			alert("Entraînement dupliqué avec succès");
		} else {
			alert("Erreur lors de la duplication: " + result.error);
		}
	};

	const handleDatePickerConfirm = () => {
		if (datePickerAction === 'addToCalendar') {
			handleAddToCalendar();
		} else if (datePickerAction === 'move') {
			handleMoveTraining();
		} else if (datePickerAction === 'duplicate') {
			handleDuplicateTraining();
		}
	};

	const openTemplateNameModal = () => {
		if (!editorTraining || !selectedStudent) return;
		setTemplateName(editorTraining.nom || "");
		setShowTemplateNameModal(true);
	};

	const closeTemplateNameModal = () => {
		setShowTemplateNameModal(false);
		setTemplateName("");
	};

	const handleSaveAsTemplate = async () => {
		if (!editorTraining || !selectedStudent || !templateName.trim()) return;

		setSavingTraining(true);
		setShowTemplateNameModal(false);

		const isCoach = templateSaveTarget === "coach";
		const targetUserId = isCoach ? user.id : selectedStudent.id;

		// Transform data to API format - filter out deleted exercises
		let ordre = 0;
		const exercicesForApi = editorTraining.exercices
			.filter((ex) => ex.supprime !== 1)
			.map((ex) => ({
				Id: -1, // New exercise
				LocalId: ex.localId || Date.now(),
				IdExercice: ex.idExercice,
				Supprime: 0,
				Ordre: ordre++,
				ListSerie: ex.series.map((s) => ({
					reps: s.reps,
					poids: s.poids,
					rpe: s.rpe || 0
				}))
			}));

		const result = await createTraining(
			targetUserId,
			templateName.trim(),
			editorTraining.date || Date.now(),
			true, // EstTemplate = true
			exercicesForApi
		);

		setSavingTraining(false);
		setTemplateName("");

		if (result.success) {
			// Refresh the relevant templates list
			if (isCoach) {
				const coachRes = await getCoachTemplates();
				if (coachRes.success) setCoachTemplates(coachRes.data?.templates || []);
			} else {
				const templatesResult = await getStudentTemplates(selectedStudent.id);
				if (templatesResult.success) setTemplates(templatesResult.templates);
			}
			setEditorTraining(null);
			setEditorMode(null);
			setTrainingModified(false);
			alert("Template enregistré avec succès");
		} else {
			alert("Erreur lors de l'enregistrement: " + result.error);
		}
	};

	// Helper to get real index from visible index
	const getRealExerciceIndex = (visibleIndex) => {
		const visibleExercises = editorTraining.exercices.filter((ex) => ex.supprime !== 1);
		const exercise = visibleExercises[visibleIndex];
		return editorTraining.exercices.findIndex(
			(ex) => (ex.localId && ex.localId === exercise.localId) || (ex.id > 0 && ex.id === exercise.id)
		);
	};

	const addSerie = (visibleIndex) => {
		if (!editorTraining) return;
		const updated = { ...editorTraining };
		const realIndex = getRealExerciceIndex(visibleIndex);
		const lastSerie = updated.exercices[realIndex].series.slice(-1)[0];
		const newSerie = {
			id: -1, // New serie, not yet saved
			reps: lastSerie ? lastSerie.reps : 10,
			poids: lastSerie ? lastSerie.poids : 0,
			rpe: lastSerie ? lastSerie.rpe || 0 : 0
		};
		updated.exercices[realIndex].series.push(newSerie);
		setEditorTraining({ ...updated });
		setTrainingModified(true);
	};

	const removeSerie = (visibleIndex) => {
		if (!editorTraining) return;
		const updated = { ...editorTraining };
		const realIndex = getRealExerciceIndex(visibleIndex);
		if (updated.exercices[realIndex].series.length > 1) {
			updated.exercices[realIndex].series.pop();
			setEditorTraining({ ...updated });
			setTrainingModified(true);
		}
	};

	const updateSerieReps = (visibleIndex, serieIndex, value) => {
		if (!editorTraining) return;
		const updated = { ...editorTraining };
		const realIndex = getRealExerciceIndex(visibleIndex);
		updated.exercices[realIndex].series[serieIndex].reps = parseInt(value) || 0;
		setEditorTraining({ ...updated });
		setTrainingModified(true);
	};

	const updateSeriePoids = (visibleIndex, serieIndex, value) => {
		if (!editorTraining) return;
		const updated = { ...editorTraining };
		const realIndex = getRealExerciceIndex(visibleIndex);
		updated.exercices[realIndex].series[serieIndex].poids = parseFloat(value) || 0;
		setEditorTraining({ ...updated });
		setTrainingModified(true);
	};

	const updateSerieRpe = (visibleIndex, serieIndex, value) => {
		if (!editorTraining) return;
		const updated = { ...editorTraining };
		const realIndex = getRealExerciceIndex(visibleIndex);
		updated.exercices[realIndex].series[serieIndex].rpe = parseInt(value) || 0;
		setEditorTraining({ ...updated });
		setTrainingModified(true);
	};

	const goToPreviousMonth = () => {
		setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
	};

	const goToNextMonth = () => {
		setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
	};

	const getDaysInMonth = (year, month) => {
		return new Date(year, month + 1, 0).getDate();
	};

	const getFirstDayOfMonth = (year, month) => {
		const day = new Date(year, month, 1).getDay();
		// Convert Sunday (0) to 7, so Monday is 1
		return day === 0 ? 7 : day;
	};

	// ========== Multi-date duplication ==========
	const startMultiDateDuplicate = (training) => {
		setMultiSelectMode(true);
		setDuplicateSourceTraining(training);
		setSelectedDates([]);
	};

	const cancelMultiDateDuplicate = () => {
		setMultiSelectMode(false);
		setDuplicateSourceTraining(null);
		setSelectedDates([]);
	};

	const toggleDateSelection = (dateTimestamp) => {
		setSelectedDates(prev =>
			prev.includes(dateTimestamp)
				? prev.filter(d => d !== dateTimestamp)
				: [...prev, dateTimestamp]
		);
	};

	const confirmMultiDateDuplicate = async () => {
		if (!duplicateSourceTraining || !selectedStudent || selectedDates.length === 0) return;
		setSavingTraining(true);
		const result = await duplicateTrainingMultiple(selectedStudent.id, duplicateSourceTraining.id, selectedDates);
		setSavingTraining(false);
		cancelMultiDateDuplicate();
		if (result.success) {
			// Refresh calendar
			const daysResult = await getTrainingDays(selectedStudent.id, currentDate.getFullYear(), currentDate.getMonth() + 1);
			if (daysResult.success) setTrainingDays(daysResult.trainingDays);
			alert(`Entraînement dupliqué sur ${selectedDates.length} date(s)`);
		} else {
			alert("Erreur: " + result.error);
		}
	};

	// ========== Planification ==========
	const openPlanification = async () => {
		setShowPlanification(true);
		setPlanSelectedTemplate(null);
		setPlanDays([false, false, false, false, false, false, false]);
		setPlanWeeks(4);
		setPlanStartDate(new Date().toISOString().split('T')[0]);
		setPlanSaveAsTemplate(false);
		// Fetch coach templates
		const result = await getCoachTemplates();
		if (result.success) setCoachTemplates(result.data?.templates || []);
	};

	const closePlanification = () => {
		setShowPlanification(false);
		setPlanSelectedTemplate(null);
	};

	const getPlanDates = () => {
		const dates = [];
		const start = new Date(planStartDate);
		// Find the Monday of the start date's week (ISO: Monday=0)
		const startDayOfWeek = (start.getDay() + 6) % 7; // 0=Mon, 6=Sun
		const weekStart = new Date(start);
		weekStart.setDate(start.getDate() - startDayOfWeek);

		// We need planWeeks full weeks of selected days FROM the start date
		// Iterate enough weeks to guarantee planWeeks occurrences
		let collected = 0;
		for (let week = 0; collected < planWeeks * 7 && week < planWeeks + 1; week++) {
			for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
				if (planDays[dayIndex]) {
					const date = new Date(weekStart);
					date.setDate(weekStart.getDate() + week * 7 + dayIndex);
					if (date >= start) {
						dates.push(date.getTime());
					}
				}
			}
			collected++;
		}

		// Keep only the first planWeeks occurrences per selected day
		// Count how many selected days per week
		const selectedDaysCount = planDays.filter(Boolean).length;
		const maxDates = planWeeks * selectedDaysCount;
		dates.sort((a, b) => a - b);
		return dates.slice(0, maxDates);
	};

	const confirmPlanification = async () => {
		if (!selectedStudent || !planSelectedTemplate) return;
		const dates = getPlanDates();
		if (dates.length === 0) { alert("Aucune date sélectionnée"); return; }

		setPlanLoading(true);
		const isCoachTemplate = planTemplateSource === "coach";

		const payload = {
			studentId: selectedStudent.id,
			dates,
			saveAsStudentTemplate: planSaveAsTemplate
		};

		if (isCoachTemplate) {
			payload.templateName = planSelectedTemplate.nom;
			payload.exercises = planSelectedTemplate.exercices.map((ex, i) => ({
				nomExercice: ex.nom || ex.nomExercice,
				ordre: i,
				supersetGroup: ex.supersetGroup || 0,
				series: ex.series.map((s, j) => ({ ordre: j, reps: s.reps || 0, poids: s.poids || 0, rpe: s.rpe || 0 }))
			}));
		} else {
			payload.templateId = planSelectedTemplate.id;
		}

		const result = await scheduleProgramming(selectedStudent.id, payload);
		setPlanLoading(false);

		if (result.success) {
			closePlanification();
			// Refresh calendar
			const daysResult = await getTrainingDays(selectedStudent.id, currentDate.getFullYear(), currentDate.getMonth() + 1);
			if (daysResult.success) setTrainingDays(daysResult.trainingDays);
			alert(`${dates.length} séances planifiées avec succès`);
		} else {
			alert("Erreur: " + result.error);
		}
	};

	// ========== Send template ==========
	const handleSendTemplate = async (template) => {
		if (!selectedStudent) return;
		const confirmed = window.confirm(`Envoyer le template "${template.nom}" à ${selectedStudent.prenom} ${selectedStudent.nom} ?`);
		if (!confirmed) return;

		// Fetch full template data
		const fullResult = await getTrainingFull(selectedStudent.id, template.id);
		if (!fullResult.success) { alert("Erreur lors de la récupération du template"); return; }

		const exercisesPayload = fullResult.training.exercices.map((ex, i) => ({
			nomExercice: ex.nom || ex.nomExercice,
			ordre: i,
			supersetGroup: ex.supersetGroup || 0,
			series: ex.series.map((s, j) => ({ ordre: j, reps: s.reps || 0, poids: s.poids || 0, rpe: s.rpe || 0 }))
		}));

		const result = await sendTemplateToStudent(selectedStudent.id, template.nom, exercisesPayload);
		if (result.success) {
			// Refresh templates
			const tResult = await getStudentTemplates(selectedStudent.id);
			if (tResult.success) setTemplates(tResult.templates);
			alert("Template envoyé avec succès");
		} else {
			alert("Erreur: " + result.error);
		}
	};

	// ========== Stats tab ==========
	const fetchStudentStats = async (period) => {
		if (!selectedStudent) return;
		setStatsLoading(true);
		const result = await getStudentStats(selectedStudent.id, period);
		if (result.success) setStatsData(result.data);
		else setStatsData(null);
		setStatsLoading(false);
	};

	const fetchExerciseProgressData = async (exerciseId, period) => {
		if (!selectedStudent || !exerciseId) return;
		const result = await getExerciseProgress(selectedStudent.id, exerciseId, period);
		if (result.success) setExerciseProgressData(result.data);
		else setExerciseProgressData(null);
	};

	// Fetch stats when switching to stats tab or changing student/period
	useEffect(() => {
		if (activeTab === "statistiques" && selectedStudent) {
			fetchStudentStats(statsPeriod);
		}
	}, [activeTab, selectedStudent, statsPeriod]);

	// Fetch exercise progress when exercise selection changes
	useEffect(() => {
		if (selectedProgressExercise) {
			fetchExerciseProgressData(selectedProgressExercise, exerciseProgressPeriod);
		} else {
			setExerciseProgressData(null);
		}
	}, [selectedProgressExercise, exerciseProgressPeriod]);

	const fetchExerciseProgressData2 = async (exerciseId, period) => {
		const result = await getExerciseProgress(selectedStudent.id, exerciseId, period);
		if (result.success) setExerciseProgressData2(result.data);
		else setExerciseProgressData2(null);
	};

	useEffect(() => {
		if (selectedProgressExercise2) {
			fetchExerciseProgressData2(selectedProgressExercise2, exerciseProgressPeriod2);
		} else {
			setExerciseProgressData2(null);
		}
	}, [selectedProgressExercise2, exerciseProgressPeriod2]);

	// ========== Overview tab ==========
	const fetchOverviewData = async () => {
		setOverviewLoading(true);
		const [heatRes, scoresRes] = await Promise.all([
			getOverviewHeatmap(overviewPeriod),
			getStudentScores(overviewPeriod)
		]);
		if (heatRes.success) setHeatmapData(heatRes.data);
		console.log("scoresRes:", JSON.stringify(scoresRes).substring(0, 500));
		if (scoresRes.success) setStudentScores(scoresRes.data?.students || []);
		setOverviewLoading(false);
	};

	useEffect(() => {
		if (activeTab === "vue-ensemble") {
			fetchOverviewData();
		}
		if (activeTab === "statistiques" && !studentScores) {
			getStudentScores(overviewPeriod).then(res => { if (res.success) setStudentScores(res.data?.students || []); });
		}
	}, [activeTab, overviewPeriod]);

	const openTrainingDetailModal = async (studentId, studentName, dateStr) => {
		setTrainingDetailLoading(true);
		setTrainingDetailModal({ studentName, trainings: [], previousTraining: null });

		const dateParts = dateStr.split("-");
		const d = { year: parseInt(dateParts[0]), month: parseInt(dateParts[1]), day: parseInt(dateParts[2]) };
		const detailRes = await getTrainingDetails(studentId, d.year, d.month, d.day);
		if (!detailRes.success || !detailRes.trainings || detailRes.trainings.length === 0) {
			setTrainingDetailModal(null);
			setTrainingDetailLoading(false);
			return;
		}

		// Fetch all trainings for that day in parallel
		const fullResults = await Promise.all(
			detailRes.trainings.map(t => getTrainingFull(studentId, t.id))
		);
		const trainings = fullResults.filter(r => r.success).map(r => r.training);
		if (trainings.length === 0) {
			setTrainingDetailModal(null);
			setTrainingDetailLoading(false);
			return;
		}

		// Find previous training day for progression comparison
		let prevTraining = null;
		const daysRes = await getTrainingDays(studentId, d.year, d.month);
		let prevDay = null;
		if (daysRes.success) {
			const sorted = daysRes.trainingDays.filter(day => day < d.day).sort((a, b) => b - a);
			if (sorted.length > 0) prevDay = { year: d.year, month: d.month, day: sorted[0] };
		}
		if (!prevDay) {
			const pm = d.month === 1 ? { year: d.year - 1, month: 12 } : { year: d.year, month: d.month - 1 };
			const pmRes = await getTrainingDays(studentId, pm.year, pm.month);
			if (pmRes.success && pmRes.trainingDays.length > 0) {
				const lastDay = pmRes.trainingDays.sort((a, b) => b - a)[0];
				prevDay = { year: pm.year, month: pm.month, day: lastDay };
			}
		}
		if (prevDay) {
			const prevDetailRes = await getTrainingDetails(studentId, prevDay.year, prevDay.month, prevDay.day);
			if (prevDetailRes.success && prevDetailRes.trainings && prevDetailRes.trainings.length > 0) {
				// Fetch all previous day trainings to build a complete exercise map for comparison
				const prevFullResults = await Promise.all(
					prevDetailRes.trainings.map(t => getTrainingFull(studentId, t.id))
				);
				const prevTrainings = prevFullResults.filter(r => r.success).map(r => r.training);
				// Merge all prev exercises into one virtual training for comparison
				prevTraining = { exercices: prevTrainings.flatMap(t => t.exercices || []) };
			}
		}

		setTrainingDetailModal({ studentName, trainings, previousTraining: prevTraining });
		setTrainingDetailLoading(false);
	};

	const renderCalendar = () => {
		const year = currentDate.getFullYear();
		const month = currentDate.getMonth();
		const daysInMonth = getDaysInMonth(year, month);
		const firstDay = getFirstDayOfMonth(year, month);

		const days = [];

		// Empty cells for days before the first day of the month
		for (let i = 1; i < firstDay; i++) {
			days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
		}

		// Days of the month
		for (let day = 1; day <= daysInMonth; day++) {
			const hasTraining = trainingDays.includes(day);
			const isToday =
				new Date().getDate() === day &&
				new Date().getMonth() === month &&
				new Date().getFullYear() === year;
			const isSelected = selectedDay === day;
			const dateTs = new Date(year, month, day).getTime();
			const isMultiSelected = multiSelectMode && selectedDates.includes(dateTs);

			days.push(
				<div
					key={day}
					className={`calendar-day ${hasTraining ? "has-training" : ""} ${isToday ? "today" : ""} ${isSelected && !multiSelectMode ? "selected" : ""} ${isMultiSelected ? "multi-selected" : ""} ${multiSelectMode ? "multi-mode" : ""}`}
					onClick={() => handleDayClick(day)}
				>
					<span className="day-number">{day}</span>
					{hasTraining && !multiSelectMode && <span className="training-dot"></span>}
					{isMultiSelected && <span className="multi-check">&#10003;</span>}
				</div>
			);
		}

		return days;
	};

	if (loading) {
		return (
			<div className="page">
				<div className="coach-container">
					<p className="coach-loading">Chargement...</p>
				</div>
			</div>
		);
	}

	if (subscriptionBlocked) {
		return (
			<div className="page">
				<div className="coach-container" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", textAlign: "center", padding: "40px" }}>
					<div style={{ fontSize: "48px", marginBottom: "20px" }}>🔒</div>
					<h2 style={{ color: "#F87B1B", marginBottom: "15px", fontSize: "24px" }}>Abonnement Pro requis</h2>
					<p style={{ color: "#b0b0b0", maxWidth: "500px", lineHeight: "1.6", marginBottom: "30px" }}>
						Le Dashboard Coach est réservé aux abonnés Pro. Passez à l'offre Pro pour gérer vos élèves depuis le web.
					</p>
					<button
						className="coach-btn-primary"
						onClick={() => navigate("/pro")}
						style={{ padding: "12px 30px", fontSize: "16px" }}
					>
						Voir les abonnements
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="page">
			{/* Global student selector */}
			<div className="coach-global-header" style={activeTab === "vue-ensemble" ? { opacity: 0.4, pointerEvents: "none" } : {}}>
				<select
					className="coach-student-select"
					value={selectedStudent?.id || ""}
					onChange={handleStudentChange}
				>
					<option value="">-- Sélectionner un élève --</option>
					{user && user.id && (
						<option key={user.id} value={user.id}>
							{user.prenom} {user.nom} (moi)
						</option>
					)}
					{students.map((student) => (
						<option key={student.id} value={student.id}>
							{student.prenom} {student.nom}
						</option>
					))}
				</select>
				{activeTab === "vue-ensemble" && <span style={{ marginLeft: 12, fontSize: 13, color: "#888", fontStyle: "italic" }}>Non applicable en vue d'ensemble</span>}
			</div>

			{/* Tab Navigation */}
			<div className="coach-tabs">
				<button className={`coach-tab ${activeTab === "editeur" ? "active" : ""}`} onClick={() => { if (multiSelectMode) cancelMultiDateDuplicate(); setActiveTab("editeur"); }}>Editeur</button>
				<button className={`coach-tab ${activeTab === "statistiques" ? "active" : ""}`} onClick={() => { if (multiSelectMode) cancelMultiDateDuplicate(); setActiveTab("statistiques"); }} disabled={!selectedStudent}>Statistiques</button>
				<button className={`coach-tab ${activeTab === "vue-ensemble" ? "active" : ""}`} onClick={() => { if (multiSelectMode) cancelMultiDateDuplicate(); setActiveTab("vue-ensemble"); }}>Vue d'ensemble</button>
			</div>

			{/* Multi-date confirmation bar */}
			{multiSelectMode && (
				<div className="multi-date-bar">
					<span>Duplication multi-dates: {selectedDates.length} date(s) sélectionnée(s)</span>
					<div className="multi-date-bar-buttons">
						<button className="multi-date-bar-btn cancel" onClick={cancelMultiDateDuplicate}>Annuler</button>
						<button className="multi-date-bar-btn confirm" onClick={confirmMultiDateDuplicate} disabled={selectedDates.length === 0 || savingTraining}>
							{savingTraining ? "Duplication..." : "Confirmer"}
						</button>
					</div>
				</div>
			)}

			{/* ===== TAB: Editeur ===== */}
			{activeTab === "editeur" && (
			<div className="coach-layout">
				{/* Column 1: Student & Calendar */}
				<div className="coach-column-left">
					<div className="coach-panel coach-panel-calendar">
						<h3 className="coach-panel-title">Calendrier</h3>
						<div className="calendar-container">
							<div className="calendar-header">
								<button className="calendar-nav-btn" onClick={goToPreviousMonth}>
									&lt;
								</button>
								<span className="calendar-month-year">
									{MONTH_NAMES[currentDate.getMonth()]} {currentDate.getFullYear()}
								</span>
								<button className="calendar-nav-btn" onClick={goToNextMonth}>
									&gt;
								</button>
							</div>
							<div className="calendar-weekdays">
								{DAY_NAMES.map((day) => (
									<div key={day} className="calendar-weekday">{day}</div>
								))}
							</div>
							<div className="calendar-grid">
								{loadingCalendar ? (
									<div className="calendar-loading">Chargement...</div>
								) : (
									renderCalendar()
								)}
							</div>
							{!selectedStudent && (
								<p className="calendar-hint">Selectionnez un eleve pour voir son calendrier</p>
							)}
						</div>

						{/* Training Overview */}
						{selectedDay && (
							<div className="training-overview">
								<div className="training-overview-title">
									Entrainement du {selectedDay} {MONTH_NAMES[currentDate.getMonth()]}
								</div>
								{loadingDetails ? (
									<div className="training-overview-empty">Chargement...</div>
								) : trainingDetails.length === 0 ? (
									<div className="training-overview-empty">Aucun entrainement</div>
								) : (
									trainingDetails.map((training) => (
										<div key={training.id} className="training-overview-item">
											<div className="training-overview-item-name">
												{training.nom || "Entrainement"}
											</div>
											<div className="training-overview-exercises">
												{training.exercices && training.exercices.map((exercice, index) => (
													<div key={index} className="training-overview-exercise">
														{exercice.nom} ({exercice.nbSeries})
													</div>
												))}
											</div>
											<div className="training-overview-buttons">
												<button
													className="training-overview-btn"
													onClick={() => openInEditor(training.id)}
												>
													Ouvrir dans l'editeur
												</button>
												<button
													className="training-overview-btn training-overview-btn-secondary"
													onClick={() => openDatePickerModal('duplicate', training)}
												>
													Dupliquer
												</button>
												<button
													className="training-overview-btn training-overview-btn-secondary"
													onClick={() => startMultiDateDuplicate(training)}
												>
													Multi-dates
												</button>
												<button
													className="training-overview-btn training-overview-btn-secondary"
													onClick={() => openDatePickerModal('move', training)}
												>
													Deplacer
												</button>
											</div>
										</div>
									))
								)}
							</div>
						)}
					</div>
				</div>

				{/* Column 2: Exercises */}
				<div className="coach-column-exercises">
					<div className="coach-panel coach-panel-exercises">
						<h3 className="coach-panel-title">Liste des exercices</h3>
						<div className="exercise-search">
							<input
								type="text"
								className="exercise-search-input"
								placeholder="Rechercher un exercice, un muscle..."
								value={exerciseSearch}
								onChange={(e) => setExerciseSearch(e.target.value)}
							/>
							{selectedStudent && (
								<button
									className="exercise-add-btn"
									onClick={openCreateExerciseModal}
									title="Ajouter un exercice"
								>
									+
								</button>
							)}
						</div>
						<div className="exercise-list">
							{!selectedStudent ? (
								<p className="exercise-list-hint">Selectionnez un eleve pour voir ses exercices</p>
							) : loadingExercises ? (
								<p className="exercise-list-loading">Chargement...</p>
							) : exercises.length === 0 ? (
								<p className="exercise-list-empty">Aucun exercice</p>
							) : (
								exercises
									.filter((exercise) => {
										const searchLower = exerciseSearch.toLowerCase();
										// Match by exercise name
										if (exercise.nomExercice.toLowerCase().includes(searchLower)) {
											return true;
										}
										// Match by muscle name
										const muscleNames = getMuscleNames(exercise.muscles);
										return muscleNames.some((muscle) => muscle.toLowerCase().includes(searchLower));
									})
									.map((exercise) => {
										const muscleNames = getMuscleNames(exercise.muscles);
										return (
											<div
												key={exercise.id}
												className="exercise-item"
												onClick={() => handleExerciseClick(exercise)}
											>
												<div className="exercise-item-name">{exercise.nomExercice}</div>
												{muscleNames.length > 0 && (
													<div className="exercise-item-muscles">
														{muscleNames.join(", ")}
													</div>
												)}
											</div>
										);
									})
							)}
						</div>
					</div>
				</div>

				{/* Column 3: Session Editor */}
				<div className="coach-column-middle">
					<div className="coach-panel coach-panel-editor">
						<div className="coach-panel-header">
							<div className="coach-panel-title-container">
								<h3 className="coach-panel-title">Editeur de seances</h3>
								{editorTraining && (
									<span className="coach-panel-subtitle">
										{editorMode === 'edit'
											? `Seance du ${new Date(editorTraining.date).toLocaleDateString('fr-FR')}`
											: editorMode === 'editTemplate'
											? `Template - ${editorTraining.nom}`
											: 'Nouvelle seance'}
									</span>
								)}
							</div>
							<div className="editor-header-buttons">
								<button className="editor-create-btn" onClick={createNewTraining}>
									Creer
								</button>
								<button className="editor-reset-btn" onClick={() => {
									if (editorTraining && trainingModified) {
										const confirmed = window.confirm("Une séance est en cours d'édition, êtes vous sûr de vouloir continuer ?");
										if (!confirmed) return;
									}
									setEditorTraining(null);
									setTrainingModified(false);
								}}>
									Reinitialiser
								</button>
							</div>
						</div>
						<div className="editor-content">
							{loadingEditor ? (
								<p className="editor-loading">Chargement...</p>
							) : !editorTraining ? (
								<p className="editor-empty">Selectionnez un entrainement pour l'editer</p>
							) : (
								<>
									<div className="editor-exercises">
										{editorTraining.exercices
											.filter((ex) => ex.supprime !== 1)
											.map((exercice, exIndex) => (
											<div
												key={exercice.localId || exercice.id}
												className={`editor-exercise ${draggedExercise === exIndex ? 'dragging' : ''}`}
												draggable
												onDragStart={(e) => handleDragStart(e, exIndex)}
												onDragOver={(e) => handleDragOver(e, exIndex)}
												onDragEnd={handleDragEnd}
											>
												<div className="editor-exercise-header">
													<div className="editor-exercise-left">
														<span className="editor-drag-handle">&#9776;</span>
														<span className="editor-exercise-name">{exercice.nom}</span>
													</div>
													<div className="editor-exercise-right">
														<div className="editor-exercise-series-control">
															<button
																className="editor-btn-small"
																onClick={() => removeSerie(exIndex)}
															>-</button>
															<span className="editor-series-count">{exercice.series.length} Series</span>
															<button
																className="editor-btn-small"
																onClick={() => addSerie(exIndex)}
															>+</button>
														</div>
														<button
															className="editor-remove-btn"
															onClick={() => removeExercice(exIndex)}
														>&times;</button>
													</div>
												</div>
												<div className="editor-series-list">
													{exercice.series.map((serie, serieIndex) => (
														<div key={serie.id || serieIndex} className="editor-serie">
															<div className="editor-serie-main">
																<input
																	type="number"
																	className="editor-serie-input"
																	value={serie.reps}
																	onChange={(e) => updateSerieReps(exIndex, serieIndex, e.target.value)}
																/>
																<span className="editor-serie-x">X</span>
																<input
																	type="number"
																	className="editor-serie-input"
																	value={serie.poids}
																	onChange={(e) => updateSeriePoids(exIndex, serieIndex, e.target.value)}
																/>
																<span className="editor-serie-unit">kg</span>
															</div>
															<div className="editor-serie-rpe-row">
																<span className="editor-serie-rpe-label">RPE</span>
																<input
																	type="number"
																	className="editor-serie-input editor-serie-input-rpe"
																	value={serie.rpe || 0}
																	min={0}
																	max={10}
																	onChange={(e) => updateSerieRpe(exIndex, serieIndex, e.target.value)}
																/>
															</div>
														</div>
													))}
												</div>
											</div>
										))}
									</div>
									<div className="editor-footer">
										<div className="editor-buttons">
											{editorMode === 'edit' ? (
												<>
													<button
														className="editor-btn editor-btn-save"
														onClick={handleSaveTraining}
														disabled={savingTraining}
													>
														{savingTraining ? "Enregistrement..." : "Enregistrer"}
													</button>
													<button className="editor-btn editor-btn-delete" onClick={handleDeleteTraining}>Supprimer</button>
												</>
											) : editorMode === 'editTemplate' ? (
												<>
													<button
														className="editor-btn editor-btn-save"
														onClick={handleSaveTemplate}
														disabled={savingTraining}
													>
														{savingTraining ? "Enregistrement..." : "Enregistrer"}
													</button>
													<button className="editor-btn editor-btn-delete" onClick={handleDeleteTemplate}>Supprimer</button>
													<button
														className="editor-btn"
														onClick={openTemplateNameModal}
														disabled={savingTraining}
													>
														Enregistrer comme template
													</button>
													<button
														className="editor-btn"
														onClick={() => openDatePickerModal('addToCalendar')}
														disabled={savingTraining}
													>
														Ajouter au calendrier
													</button>
												</>
											) : (
												<>
													<button
														className="editor-btn"
														onClick={openTemplateNameModal}
														disabled={savingTraining}
													>
														{savingTraining ? "Enregistrement..." : "Enregistrer comme template"}
													</button>
													<button
														className="editor-btn"
														onClick={() => openDatePickerModal('addToCalendar')}
														disabled={savingTraining}
													>
														Ajouter au calendrier
													</button>
												</>
											)}
										</div>
										<button
											className="editor-note-btn"
											onClick={openCommentModal}
											title={editorTraining?.commentaire ? "Modifier la note" : "Ajouter une note"}
										>
											{editorTraining?.commentaire ? "📝" : "📋"}
										</button>
									</div>
								</>
							)}
						</div>
					</div>
				</div>

				{/* Column 4: Templates */}
				<div className="coach-column-right">
					<div className="coach-panel coach-panel-templates">
						<div className="templates-header">
							<h3 className="coach-panel-title">Templates</h3>
							{selectedStudent && (
								<button className="coach-btn-planification" onClick={openPlanification}>Planifier</button>
							)}
						</div>
						{selectedStudent && !isSelf && (
							<div className="template-list-tabs">
								<button className={`template-list-tab ${templateListTab === "student" ? "active" : ""}`} onClick={() => setTemplateListTab("student")}>{selectedStudent.prenom}</button>
								<button className={`template-list-tab ${templateListTab === "coach" ? "active" : ""}`} onClick={() => setTemplateListTab("coach")}>Mes templates</button>
							</div>
						)}
						<div className="template-list">
							{!selectedStudent ? (
								<p className="template-list-hint">Selectionnez un eleve pour voir ses templates</p>
							) : loadingTemplates ? (
								<p className="template-list-loading">Chargement...</p>
							) : (() => {
								const displayedTemplates = (templateListTab === "student" && !isSelf) ? templates : coachTemplates;
								return displayedTemplates.length === 0 ? (
									<p className="template-list-empty">Aucun template</p>
								) : (
									displayedTemplates.map((template) => (
										<div key={template.id} className="template-item">
											<div className="template-item-header" onClick={() => openInEditor(template.id, true)}>
												<div className="template-item-icon">&#9776;</div>
												<div className="template-item-info">
													<div className="template-item-name">{template.nom}</div>
													{template.exercices && template.exercices.length > 0 && (
														<div className="template-item-exercises">
															{template.exercices.join(' • ')}
														</div>
													)}
												</div>
											</div>
											{templateListTab === "coach" && !isSelf && (
												<div className="template-item-actions">
													<button className="template-action-btn" onClick={() => handleSendTemplate(template)} title="Envoyer à l'élève">Envoyer</button>
												</div>
											)}
										</div>
									))
								);
							})()}
						</div>
					</div>
				</div>
			</div>
			)}

			{/* ===== TAB: Statistiques ===== */}
			{activeTab === "statistiques" && selectedStudent && (
			<div className="coach-stats-tab">
				<div className="coach-stats-header">
					<h2>Statistiques de {selectedStudent.prenom} {selectedStudent.nom}</h2>
					<div className="stats-period-selector">
						<button className={`stats-period-btn ${statsPeriod === 30 ? "active" : ""}`} onClick={() => setStatsPeriod(30)}>30 jours</button>
						<button className={`stats-period-btn ${statsPeriod === 90 ? "active" : ""}`} onClick={() => setStatsPeriod(90)}>90 jours</button>
					</div>
				</div>

				{statsLoading ? (
					<div className="stats-loading">Chargement des statistiques...</div>
				) : !statsData ? (
					<div className="stats-empty">Aucune donnée disponible</div>
				) : (
					<>
						{/* Summary cards */}
						{(() => {
							const regScore = statsData.regularityScore;
							const progScore = statsData.progressionScore;
							const regColor = regScore < 0 ? "#fff" : regScore >= 75 ? "#4ade80" : regScore >= 50 ? "#F87B1B" : "#ef4444";
							const progColor = progScore < 0 ? "#fff" : progScore >= 60 ? "#4ade80" : progScore >= 40 ? "#facc15" : progScore >= 20 ? "#F87B1B" : "#ef4444";
							const regrScore = statsData.regressionScore;
							const regrColor = regrScore < 0 ? "#fff" : regrScore <= 10 ? "#4ade80" : regrScore <= 25 ? "#facc15" : regrScore <= 40 ? "#F87B1B" : "#ef4444";
							const sessPerWeek = statsData.averageSessionsPerWeek;
							const sessColor = sessPerWeek >= 4 ? "#4ade80" : sessPerWeek >= 3 ? "#4ade80" : sessPerWeek >= 2 ? "#facc15" : sessPerWeek >= 1 ? "#F87B1B" : "#ef4444";
							const daysSince = statsData.daysSinceLastTraining;
							const daysColor = daysSince <= 2 ? "#4ade80" : daysSince <= 4 ? "#facc15" : daysSince <= 7 ? "#F87B1B" : "#ef4444";
							return (
								<div className="stats-summary-cards">
									<div className="stats-card">
										<div className="stats-card-value" style={{ color: "#fff" }}>{statsData.totalTrainings}</div>
										<div className="stats-card-label">Entraînements</div>
									</div>
									<div className="stats-card">
										<div className="stats-card-value" style={{ color: regColor }}>{regScore >= 0 ? regScore + "%" : "—"}</div>
										<div className="stats-card-label">Régularité</div>
									</div>
									<div className="stats-card">
										<div className="stats-card-value" style={{ color: progColor }}>{progScore >= 0 ? progScore + "%" : "—"}</div>
										<div className="stats-card-label">Progression</div>
										{progScore >= 0 && (
											<div className="stats-card-detail">{statsData.progressionSetsProgressed}/{statsData.progressionSetsAnalyzed} séries</div>
										)}
									</div>
									<div className="stats-card">
										<div className="stats-card-value" style={{ color: regrColor }}>{regrScore >= 0 ? regrScore + "%" : "—"}</div>
										<div className="stats-card-label">Régression</div>
										{regrScore >= 0 && (
											<div className="stats-card-detail">{statsData.regressionSetsRegressed}/{statsData.regressionSetsAnalyzed} séries</div>
										)}
									</div>
									<div className="stats-card">
										<div className="stats-card-value" style={{ color: sessColor }}>{sessPerWeek?.toFixed(1)}</div>
										<div className="stats-card-label">Séances / semaine</div>
									</div>
									<div className="stats-card">
										<div className="stats-card-value" style={{ color: daysColor }}>{daysSince}</div>
										<div className="stats-card-label">Jours depuis dernier entr.</div>
									</div>
								</div>
							);
						})()}


						{/* Bottom row: progressions/regressions + exercise progress */}
						<div className="stats-charts-row">
						<div className="stats-prog-regr-row">
						{/* Progressions list */}
						{statsData.progressions && statsData.progressions.length > 0 && (
							<div className="stats-progressions">
								<h3>Progressions récentes</h3>
								<div className="progressions-list">
									{statsData.progressions.map((p, i) => {
										const isWeightProg = p.poids > p.dernierPoids;
										const isRepsProg = p.reps > p.derniereReps;
										let detail;
										if (isWeightProg && isRepsProg) {
											detail = `${p.dernierPoids}kg → ${p.poids}kg | ${p.derniereReps} → ${p.reps} reps`;
										} else if (isWeightProg) {
											detail = `${p.dernierPoids}kg → ${p.poids}kg (${p.reps} reps)`;
										} else {
											detail = `${p.derniereReps} → ${p.reps} reps @ ${p.poids}kg`;
										}
										const percent = isWeightProg
											? ((p.poids - p.dernierPoids) / p.dernierPoids * 100)
											: ((p.reps - p.derniereReps) / p.derniereReps * 100);
										const date = p.date ? new Date(p.date).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) : "";
										return (
										<div key={i} className="progression-item">
											<span className="progression-name">{p.nomExercice} — <span style={{opacity: 0.5, fontSize: "0.85em"}}>Série {p.ordre + 1}</span></span>
											<span className="progression-detail">{detail}</span>
											<span className="progression-date">{date}</span>
											<span className="progression-percent positive">+{percent.toFixed(1)}%</span>
										</div>
										);
									})}
								</div>
							</div>
						)}

						{/* Regressions list */}
						{statsData.regressions && statsData.regressions.length > 0 && (
							<div className="stats-progressions stats-regressions">
								<h3>Régressions récentes</h3>
								<div className="progressions-list">
									{statsData.regressions.map((p, i) => {
										const isWeightRegr = p.poids < p.dernierPoids;
										const isRepsRegr = p.reps < p.derniereReps;
										let detail;
										if (isWeightRegr && isRepsRegr) {
											detail = `${p.dernierPoids}kg → ${p.poids}kg | ${p.derniereReps} → ${p.reps} reps`;
										} else if (isWeightRegr) {
											detail = `${p.dernierPoids}kg → ${p.poids}kg (${p.reps} reps)`;
										} else {
											detail = `${p.derniereReps} → ${p.reps} reps @ ${p.poids}kg`;
										}
										const percent = isWeightRegr
											? ((p.poids - p.dernierPoids) / p.dernierPoids * 100)
											: ((p.reps - p.derniereReps) / p.derniereReps * 100);
										const date = p.date ? new Date(p.date).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) : "";
										return (
										<div key={i} className="progression-item">
											<span className="progression-name">{p.nomExercice} — <span style={{opacity: 0.5, fontSize: "0.85em"}}>Série {p.ordre + 1}</span></span>
											<span className="progression-detail">{detail}</span>
											<span className="progression-date">{date}</span>
											<span className="progression-percent negative">{percent.toFixed(1)}%</span>
										</div>
										);
									})}
								</div>
							</div>
						)}
						</div>

						{/* Exercise progress chart */}
						<div className="stats-exercise-compare">
							{[
								{ data: exerciseProgressData, selected: selectedProgressExercise, setSelected: setSelectedProgressExercise, period: exerciseProgressPeriod, setPeriod: setExerciseProgressPeriod },
								{ data: exerciseProgressData2, selected: selectedProgressExercise2, setSelected: setSelectedProgressExercise2, period: exerciseProgressPeriod2, setPeriod: setExerciseProgressPeriod2 }
							].map((slot, slotIdx) => (
								<div key={slotIdx} className="stats-exercise-progress">
									<h3>Progression par exercice {slotIdx === 1 ? "(comparaison)" : ""}</h3>
									<div className="exercise-progress-controls">
										<select
											className="coach-student-select"
											value={slot.selected || ""}
											onChange={(e) => slot.setSelected(e.target.value ? parseInt(e.target.value) : null)}
										>
											<option value="">-- Choisir un exercice --</option>
											{exercises.map(ex => (
												<option key={ex.id} value={ex.id}>{ex.nomExercice}</option>
											))}
										</select>
										<div className="stats-period-selector">
											<button className={`stats-period-btn ${slot.period === 30 ? "active" : ""}`} onClick={() => slot.setPeriod(30)}>30j</button>
											<button className={`stats-period-btn ${slot.period === 90 ? "active" : ""}`} onClick={() => slot.setPeriod(90)}>90j</button>
											<button className={`stats-period-btn ${slot.period === 180 ? "active" : ""}`} onClick={() => slot.setPeriod(180)}>180j</button>
										</div>
									</div>
									{slot.data && slot.data.points && slot.data.points.length > 0 ? (
										<div className="stats-charts-row">
											<div className="stats-chart-container">
												<h3>Charges (kg)</h3>
												<Line
													data={{
														labels: slot.data.points.map(p => p.dateLabel),
														datasets: [{
															label: "Charge max",
															data: slot.data.points.map(p => p.maxWeight),
															borderColor: "#F87B1B",
															backgroundColor: "rgba(248, 123, 27, 0.1)",
															tension: 0.3,
															fill: true,
															pointBackgroundColor: "#F87B1B",
															pointRadius: 4
														}, {
															label: "Charge moyenne",
															data: slot.data.points.map(p => p.avgWeight),
															borderColor: "#30a49a",
															backgroundColor: "rgba(48, 164, 154, 0.08)",
															tension: 0.3,
															fill: true,
															pointBackgroundColor: "#30a49a",
															pointRadius: 3,
															borderDash: [5, 3]
														}]
													}}
													options={{
														responsive: true,
														plugins: { legend: { labels: { color: "#b0b0b0" } } },
														scales: {
															y: { ticks: { color: "#b0b0b0" }, grid: { color: "rgba(255,255,255,0.05)" } },
															x: { ticks: { color: "#b0b0b0" }, grid: { display: false } }
														}
													}}
												/>
											</div>
											<div className="stats-chart-container">
												<h3>Répétitions</h3>
												<Line
													data={{
														labels: slot.data.points.map(p => p.dateLabel),
														datasets: [{
															label: "Reps max",
															data: slot.data.points.map(p => p.maxReps),
															borderColor: "#F87B1B",
															backgroundColor: "rgba(248, 123, 27, 0.1)",
															tension: 0.3,
															fill: true,
															pointBackgroundColor: "#F87B1B",
															pointRadius: 4
														}, {
															label: "Reps moyenne",
															data: slot.data.points.map(p => p.avgReps),
															borderColor: "#30a49a",
															backgroundColor: "rgba(48, 164, 154, 0.08)",
															tension: 0.3,
															fill: true,
															pointBackgroundColor: "#30a49a",
															pointRadius: 3,
															borderDash: [5, 3]
														}]
													}}
													options={{
														responsive: true,
														plugins: { legend: { labels: { color: "#b0b0b0" } } },
														scales: {
															y: { beginAtZero: true, ticks: { stepSize: 1, color: "#b0b0b0" }, grid: { color: "rgba(255,255,255,0.05)" } },
															x: { ticks: { color: "#b0b0b0" }, grid: { display: false } }
														}
													}}
												/>
											</div>
										</div>
									) : slot.selected ? (
										<div className="stats-empty">Aucune donnée pour cet exercice</div>
									) : null}
								</div>
							))}
						</div>
						</div>

					</>
				)}
			</div>
			)}

			{/* ===== TAB: Vue d'ensemble ===== */}
			{activeTab === "vue-ensemble" && (
			<div className="coach-overview-tab">
				{overviewLoading ? (
					<div className="stats-loading">Chargement...</div>
				) : (
					<>
						{/* Student filter */}
						<div className="overview-filter">
							<label>Filtrer par élève :</label>
							<select
								className="overview-filter-select"
								value={overviewFilterStudent || ""}
								onChange={(e) => { setOverviewFilterStudent(e.target.value ? parseInt(e.target.value) : null); setSelectedHeatmapDay(null); }}
							>
								<option value="">Tous les élèves</option>
								{user && user.id && <option key={user.id} value={user.id}>{user.prenom} {user.nom} (moi)</option>}
								{students.map(s => (
									<option key={s.id} value={s.id}>{s.prenom} {s.nom}</option>
								))}
							</select>
							<label style={{ marginLeft: 24 }}>Période :</label>
							<select
								className="overview-filter-select"
								value={overviewPeriod}
								onChange={(e) => setOverviewPeriod(parseInt(e.target.value))}
							>
								<option value={30}>30 jours</option>
								<option value={90}>90 jours</option>
							</select>
						</div>

						{/* Weekly Heatmap */}
						{heatmapData && heatmapData.students && heatmapData.students.length > 0 && (() => {
							// Compute week (Mon-Sun) with offset
							const today = new Date();
							const dayOfWeek = today.getDay(); // 0=Sun
							const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
							const monday = new Date(today);
							monday.setDate(today.getDate() + mondayOffset + (heatmapWeekOffset * 7));
							monday.setHours(0, 0, 0, 0);

							const weekDays = [];
							for (let i = 0; i < 7; i++) {
								const d = new Date(monday);
								d.setDate(monday.getDate() + i);
								weekDays.push(d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0'));
							}

							const weekDayLabels = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

							// Filter by selected student if any
							const filteredStudents = overviewFilterStudent
								? heatmapData.students.filter(s => s.id === overviewFilterStudent)
								: heatmapData.students;

							// For each student, get their training count per day this week
							const weekData = filteredStudents.map(student => {
								const dayMap = {};
								(student.days || []).forEach(d => { dayMap[d.date] = d.count; });
								return {
									...student,
									weekCounts: weekDays.map(date => dayMap[date] || 0),
									totalWeek: weekDays.reduce((sum, date) => sum + (dayMap[date] || 0), 0)
								};
							});

							// For selected day, find students who trained
							const selectedDayStudents = selectedHeatmapDay !== null
								? weekData.filter(s => s.weekCounts[selectedHeatmapDay] > 0)
								: null;

							const isCurrentWeek = heatmapWeekOffset === 0;
							const sundayDate = new Date(monday);
							sundayDate.setDate(monday.getDate() + 6);
							const weekLabel = `${monday.getDate()} ${monday.toLocaleDateString('fr-FR', { month: 'short' })} — ${sundayDate.getDate()} ${sundayDate.toLocaleDateString('fr-FR', { month: 'short' })}`;

							return (
								<div className="overview-heatmap">
									<div className="week-heatmap-nav">
										<button
											className="week-nav-arrow"
											onClick={() => { setHeatmapWeekOffset(prev => Math.max(prev - 1, -Math.floor(overviewPeriod / 7))); setSelectedHeatmapDay(null); }}
											disabled={heatmapWeekOffset <= -Math.floor(overviewPeriod / 7)}
										>◀</button>
										<h2>{isCurrentWeek ? "Activité de la semaine" : weekLabel}</h2>
										<button
											className="week-nav-arrow"
											onClick={() => { setHeatmapWeekOffset(prev => Math.min(prev + 1, 0)); setSelectedHeatmapDay(null); }}
											disabled={isCurrentWeek}
										>▶</button>
									</div>
									<div className="week-heatmap">
										<div className="week-heatmap-header">
											{weekDayLabels.map((label, i) => {
												const isToday = weekDays[i] === (today.getFullYear() + '-' + String(today.getMonth()+1).padStart(2,'0') + '-' + String(today.getDate()).padStart(2,'0'));
												const isSelected = selectedHeatmapDay === i;
												// Total trainings this day across all students
												const dayTotal = weekData.reduce((sum, s) => sum + s.weekCounts[i], 0);
												return (
													<div
														key={i}
														className={`week-day-col ${isToday ? "today" : ""} ${isSelected ? "selected" : ""}`}
														onClick={() => setSelectedHeatmapDay(isSelected ? null : i)}
													>
														<div className="week-day-label">{label}</div>
														<div className="week-day-date">{parseInt(weekDays[i].split("-")[2])}</div>
														<div className={`week-day-count ${dayTotal === 0 ? "zero" : ""}`}>{dayTotal}</div>
														<div className="week-day-bar">
															<div
																className="week-day-bar-fill"
																style={{ height: `${Math.min(dayTotal / Math.max(weekData.length, 1) * 100, 100)}%` }}
															/>
														</div>
													</div>
												);
											})}
										</div>

									</div>

									{/* Expanded day detail */}
									{selectedDayStudents !== null && (
										<div className="week-day-detail">
											<h3>{weekDayLabels[selectedHeatmapDay]} {parseInt(weekDays[selectedHeatmapDay].split("-")[2])} — {selectedDayStudents.length} élève(s) entraîné(s)</h3>
											{selectedDayStudents.length === 0 ? (
												<div className="stats-empty">Aucun entraînement ce jour</div>
											) : (
												<div className="week-day-detail-list">
													{selectedDayStudents.map(s => (
														<div key={s.id} className="week-day-detail-item">
															<span className="week-day-detail-name">{s.prenom} {s.nom}</span>
															<span className="week-day-detail-count">{s.weekCounts[selectedHeatmapDay]} séance(s)</span>
															<button className="alert-action-btn" onClick={() => openTrainingDetailModal(s.id, `${s.prenom} ${s.nom}`, weekDays[selectedHeatmapDay])}>Voir</button>
														</div>
													))}
												</div>
											)}
										</div>
									)}
								</div>
							);
						})()}

						{/* Student Scores List */}
						{studentScores && studentScores.length > 0 && (() => {
							const getRegularityColor = (score) => {
								if (score >= 90) return "#4ade80";
								if (score >= 75) return "#4ade80";
								if (score >= 50) return "#F87B1B";
								return "#ef4444";
							};
							const getProgressionColor = (score) => {
								if (score < 0) return "rgba(255,255,255,0.3)";
								if (score >= 60) return "#4ade80";
								if (score >= 40) return "#facc15";
								if (score >= 20) return "#F87B1B";
								return "#ef4444";
							};
							const getRegressionColor = (score) => {
								if (score < 0) return "rgba(255,255,255,0.3)";
								if (score <= 10) return "#4ade80";
								if (score <= 25) return "#facc15";
								if (score <= 40) return "#F87B1B";
								return "#ef4444";
							};

							const getSessionsColor = (avg) => {
								if (avg >= 3) return "#4ade80";
								if (avg >= 2) return "#facc15";
								if (avg >= 1) return "#F87B1B";
								return "#ef4444";
							};
							const getDaysColor = (days) => {
								if (days <= 2) return "#4ade80";
								if (days <= 4) return "#facc15";
								if (days <= 7) return "#F87B1B";
								return "#ef4444";
							};

							const sorted = [...studentScores].sort((a, b) => {
								if (scoresSortBy === "name") return (a.prenom + a.nom).localeCompare(b.prenom + b.nom);
								if (scoresSortBy === "regularity") return a.regularityScore - b.regularityScore;
								if (scoresSortBy === "progression") return (a.progressionScore < 0 ? -999 : a.progressionScore) - (b.progressionScore < 0 ? -999 : b.progressionScore);
								if (scoresSortBy === "regression") return (a.regressionScore < 0 ? -999 : a.regressionScore) - (b.regressionScore < 0 ? -999 : b.regressionScore);
								if (scoresSortBy === "lastTraining") return (b.daysSinceLastTraining ?? 999) - (a.daysSinceLastTraining ?? 999);
								if (scoresSortBy === "sessions") return (a.avgSessions ?? 0) - (b.avgSessions ?? 0);
								if (scoresSortBy === "trainings") return (a.totalTrainings ?? 0) - (b.totalTrainings ?? 0);
								return 0;
							});

							const maxWeekly = Math.max(...studentScores.flatMap(s => s.weeklySessions || []), 1);

							return (
								<div className="scores-section">
									<div className="scores-header">
										<h2>Suivi des élèves</h2>
										<div className="scores-sort">
											<span>Trier :</span>
											{[
												{ key: "regularity", label: "Régularité" },
												{ key: "trainings", label: "Entraînements" },
												{ key: "sessions", label: "Séances/sem" },
												{ key: "progression", label: "Progression" },
												{ key: "regression", label: "Régression" },
												{ key: "lastTraining", label: "Activité" },
												{ key: "name", label: "Nom" }
											].map(opt => (
												<button
													key={opt.key}
													className={`scores-sort-btn ${scoresSortBy === opt.key ? "active" : ""}`}
													onClick={() => setScoresSortBy(opt.key)}
												>{opt.label}</button>
											))}
										</div>
									</div>
									<div className="scores-list">
										{sorted.map(s => (
											<div key={s.id} className={`score-row ${(s.daysSinceLastTraining ?? 999) > 7 ? "inactive" : ""} ${overviewFilterStudent === s.id ? "selected" : ""}`}
												onClick={() => { setOverviewFilterStudent(overviewFilterStudent === s.id ? null : s.id); setSelectedHeatmapDay(null); }}
											>
												<div className="score-row-name">{s.prenom} {s.nom?.charAt(0)}.</div>
												<div className="score-row-metric">
													<div className="score-row-metric-label">Entraînements</div>
													<div className="score-row-metric-value">
														<span className="score-number" style={{ color: "#fff" }}>{s.totalTrainings || 0}</span>
													</div>
												</div>
												<div className="score-row-metric">
													<div className="score-row-metric-label">Séances/sem</div>
													<div className="score-row-metric-value">
														<span className="score-dot" style={{ background: getSessionsColor(s.avgSessions ?? 0) }}></span>
														<span className="score-number">{(s.avgSessions ?? 0).toFixed(1)}</span>
													</div>
												</div>
												<div className="score-row-metric">
													<div className="score-row-metric-label">Régularité</div>
													<div className="score-row-metric-value">
														<span className="score-dot" style={{ background: getRegularityColor(s.regularityScore) }}></span>
														<span className="score-number">{s.regularityScore}</span>
													</div>
													<div className="score-mini-bars">
														{(s.weeklySessions || []).map((count, i) => (
															<div key={i} className="score-mini-bar">
																<div className="score-mini-bar-fill" style={{ height: `${(count / maxWeekly) * 100}%` }}></div>
															</div>
														))}
													</div>
												</div>
												<div className="score-row-metric">
													<div className="score-row-metric-label">Progression</div>
													<div className="score-row-metric-value">
														<span className="score-dot" style={{ background: getProgressionColor(s.progressionScore) }}></span>
														<span className="score-number">{s.progressionScore >= 0 ? s.progressionScore + "%" : "—"}</span>
													</div>
													{s.progressionScore >= 0 && (
														<div className="score-row-metric-detail">{s.progressedSets}/{s.setsAnalyzed} séries</div>
													)}
												</div>
												<div className="score-row-metric">
													<div className="score-row-metric-label">Régression</div>
													<div className="score-row-metric-value">
														<span className="score-dot" style={{ background: getRegressionColor(s.regressionScore) }}></span>
														<span className="score-number">{s.regressionScore >= 0 ? s.regressionScore + "%" : "—"}</span>
													</div>
													{s.regressionScore >= 0 && (
														<div className="score-row-metric-detail">{s.regressedSets}/{s.regressionSetsAnalyzed} séries</div>
													)}
												</div>
												<div className="score-row-metric">
													<div className="score-row-metric-label">Dernier entr.</div>
													<div className="score-row-metric-value">
														<span className="score-dot" style={{ background: s.daysSinceLastTraining != null ? getDaysColor(s.daysSinceLastTraining) : "rgba(255,255,255,0.3)" }}></span>
														<span className="score-number">
															{s.daysSinceLastTraining != null
																? (s.daysSinceLastTraining === 0 ? "Auj." : s.daysSinceLastTraining + "j")
																: "—"
															}
														</span>
													</div>
												</div>
												<button className="alert-action-btn" onClick={(e) => {
													e.stopPropagation();
													const student = students.find(st => st.id === s.id);
													if (student) { setSelectedStudent(student); setActiveTab("editeur"); }
												}}>Ouvrir dans l'éditeur</button>
											</div>
										))}
									</div>
								</div>
							);
						})()}
						{studentScores && studentScores.length === 0 && (
							<div className="stats-empty">Aucun élève</div>
						)}
					</>
				)}
			</div>
			)}

			{/* Planification Modal */}
			{showPlanification && (
				<div className="modal-overlay" onClick={closePlanification}>
					<div className="modal-content planification-modal" onClick={(e) => e.stopPropagation()}>
						<button className="modal-close" onClick={closePlanification}>&times;</button>
						<div className="modal-inner">
							<h2 className="modal-title">Planification</h2>
							<div className="modal-divider"></div>

							<div className="plan-layout">
								{/* Left: Template selection */}
								<div className="plan-templates">
									<h3>Choisir un template</h3>
									{!isSelf && (
										<div className="plan-template-source">
											<button className={`stats-period-btn ${planTemplateSource === "student" ? "active" : ""}`} onClick={() => setPlanTemplateSource("student")}>Templates élève</button>
											<button className={`stats-period-btn ${planTemplateSource === "coach" ? "active" : ""}`} onClick={() => setPlanTemplateSource("coach")}>Mes templates</button>
										</div>
									)}
									<div className="plan-template-list">
										{(planTemplateSource === "student" ? templates : coachTemplates).map(t => (
											<div
												key={t.id}
												className={`plan-template-item ${planSelectedTemplate?.id === t.id ? "selected" : ""}`}
												onClick={() => setPlanSelectedTemplate(t)}
											>
												{t.nom}
											</div>
										))}
										{(planTemplateSource === "student" ? templates : coachTemplates).length === 0 && (
											<div className="stats-empty">Aucun template</div>
										)}
									</div>
								</div>

								{/* Right: Schedule config */}
								<div className="plan-config">
									<h3>Configuration</h3>
									<div className="plan-field">
										<label>Date de début</label>
										<input type="date" className="plan-date-input" value={planStartDate} onChange={(e) => setPlanStartDate(e.target.value)} />
									</div>
									<div className="plan-field">
										<label>Nombre de semaines</label>
										<input type="number" className="plan-weeks-input" min={1} max={52} value={planWeeks} onChange={(e) => setPlanWeeks(parseInt(e.target.value) || 1)} />
									</div>
									<div className="plan-field">
										<label>Jours de la semaine</label>
										<div className="plan-days-row">
											{DAY_NAMES.map((name, i) => (
												<button
													key={i}
													className={`plan-day-btn ${planDays[i] ? "active" : ""}`}
													onClick={() => {
														const newDays = [...planDays];
														newDays[i] = !newDays[i];
														setPlanDays(newDays);
													}}
												>
													{name}
												</button>
											))}
										</div>
									</div>
									{planTemplateSource === "coach" && !isSelf && (
									<div className="plan-field">
										<label>
											<input type="checkbox" checked={planSaveAsTemplate} onChange={(e) => setPlanSaveAsTemplate(e.target.checked)} />
											{" "}Sauvegarder comme template de l'élève
										</label>
									</div>
								)}
									<div className="plan-preview">
										<strong>{getPlanDates().length} séances</strong> seront créées
									</div>
									<button
										className="modal-btn"
										onClick={confirmPlanification}
										disabled={!planSelectedTemplate || getPlanDates().length === 0 || planLoading}
									>
										{planLoading ? "Planification..." : "Confirmer la planification"}
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Exercise Modal */}
			{selectedExercise && (
				<div className="modal-overlay" onClick={closeExerciseModal}>
					<div className="modal-content" onClick={(e) => e.stopPropagation()}>
						<button className="modal-close" onClick={closeExerciseModal}>&times;</button>
						<div className="modal-inner">
							{exerciseEditMode ? (
								<>
									<h2 className="modal-title">Modifier l'exercice</h2>
									<div className="modal-divider"></div>
									<div className="exercise-edit-form">
										<div className="exercise-edit-field">
											<label className="exercise-edit-label">Nom</label>
											<input
												type="text"
												className="exercise-edit-input"
												value={editingExerciseName}
												onChange={(e) => setEditingExerciseName(e.target.value)}
												placeholder="Nom de l'exercice"
											/>
										</div>
										<div className="exercise-edit-field">
											<label className="exercise-edit-label">Description</label>
											<textarea
												className="exercise-edit-textarea"
												value={editingExerciseDescription}
												onChange={(e) => setEditingExerciseDescription(e.target.value)}
												placeholder="Description de l'exercice"
												rows={4}
											/>
										</div>
										<div className="exercise-edit-field">
											<label className="exercise-edit-label">Muscles</label>
											<div className="exercise-edit-muscles">
												{MUSCLE_LABELS.map((muscle, index) => (
													<div
														key={index}
														className={`exercise-edit-muscle-chip ${editingExerciseMuscles[index] === 1 ? 'selected' : ''}`}
														onClick={() => toggleMuscle(index)}
													>
														{muscle}
													</div>
												))}
											</div>
										</div>
									</div>
									<div className="exercise-edit-buttons">
										<button
											className="modal-btn modal-btn-secondary"
											onClick={cancelEditExercise}
											disabled={savingExercise}
										>
											Annuler
										</button>
										<button
											className="modal-btn"
											onClick={saveExerciseChanges}
											disabled={savingExercise || !editingExerciseName.trim()}
										>
											{savingExercise ? "Enregistrement..." : "Enregistrer"}
										</button>
									</div>
								</>
							) : (
								<>
									<h2 className="modal-title">{selectedExercise.nomExercice}</h2>
									{getMuscleNames(selectedExercise.muscles).length > 0 && (
										<div className="modal-muscles">
											{getMuscleNames(selectedExercise.muscles).map((muscle, index) => (
												<span key={index} className="modal-muscle-chip">{muscle}</span>
											))}
										</div>
									)}
									<div className="modal-divider"></div>
									<div className="modal-section">
										<div className="modal-section-header">
											<span className="modal-section-title">Instructions</span>
										</div>
										{selectedExercise.comExercice ? (
											<p className="modal-description">{selectedExercise.comExercice}</p>
										) : (
											<p className="modal-description modal-no-description">Aucune description</p>
										)}
									</div>
									<div className="exercise-modal-buttons">
										<button className="modal-btn modal-btn-secondary" onClick={startEditExercise}>
											Editer
										</button>
										<button className="modal-btn" onClick={handleAddToSession}>
											Ajouter a la seance
										</button>
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			)}

			{/* Date Picker Modal */}
			{showDatePicker && (
				<div className="modal-overlay" onClick={closeDatePickerModal}>
					<div className="modal-content date-picker-modal" onClick={(e) => e.stopPropagation()}>
						<button className="modal-close" onClick={closeDatePickerModal}>&times;</button>
						<div className="modal-inner">
							<h2 className="modal-title">Choisir une date</h2>
							<div className="modal-divider"></div>
							<div className="date-picker-calendar">
								<div className="date-picker-header">
									<button
										className="calendar-nav-btn"
										onClick={() => setSelectedCalendarDate(new Date(selectedCalendarDate.getFullYear(), selectedCalendarDate.getMonth() - 1, 1))}
									>
										&lt;
									</button>
									<span className="calendar-month-year">
										{MONTH_NAMES[selectedCalendarDate.getMonth()]} {selectedCalendarDate.getFullYear()}
									</span>
									<button
										className="calendar-nav-btn"
										onClick={() => setSelectedCalendarDate(new Date(selectedCalendarDate.getFullYear(), selectedCalendarDate.getMonth() + 1, 1))}
									>
										&gt;
									</button>
								</div>
								<div className="calendar-weekdays">
									{DAY_NAMES.map((day) => (
										<div key={day} className="calendar-weekday">{day}</div>
									))}
								</div>
								<div className="calendar-grid">
									{(() => {
										const year = selectedCalendarDate.getFullYear();
										const month = selectedCalendarDate.getMonth();
										const daysInMonth = getDaysInMonth(year, month);
										const firstDay = getFirstDayOfMonth(year, month);
										const days = [];

										for (let i = 1; i < firstDay; i++) {
											days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
										}

										for (let day = 1; day <= daysInMonth; day++) {
											const isSelected = selectedCalendarDate.getDate() === day;
											const isToday = new Date().getDate() === day &&
												new Date().getMonth() === month &&
												new Date().getFullYear() === year;

											days.push(
												<div
													key={day}
													className={`calendar-day ${isToday ? "today" : ""} ${isSelected ? "selected" : ""}`}
													onClick={() => setSelectedCalendarDate(new Date(year, month, day))}
												>
													<span className="day-number">{day}</span>
												</div>
											);
										}

										return days;
									})()}
								</div>
							</div>
							<div className="date-picker-selected">
								Date sélectionnée: {selectedCalendarDate.getDate()} {MONTH_NAMES[selectedCalendarDate.getMonth()]} {selectedCalendarDate.getFullYear()}
							</div>
							<button className="modal-btn" onClick={handleDatePickerConfirm}>
								Confirmer
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Training Detail Modal */}
			{trainingDetailModal && (
				<div className="modal-overlay" onClick={() => setTrainingDetailModal(null)}>
					<div className="modal-content training-detail-modal" onClick={(e) => e.stopPropagation()}>
						<button className="modal-close" onClick={() => setTrainingDetailModal(null)}>&times;</button>
						<div className="modal-inner">
							<h2 className="modal-title">{trainingDetailModal.studentName}</h2>
							<div className="modal-divider"></div>
							{trainingDetailLoading ? (
								<div className="stats-loading">Chargement...</div>
							) : trainingDetailModal.trainings && trainingDetailModal.trainings.length > 0 ? (
								<div className="training-detail-all">
									{trainingDetailModal.trainings.map((training, tIdx) => (
										<div key={tIdx} className="training-detail-block">
											{trainingDetailModal.trainings.length > 1 && (
												<h3 className="training-detail-block-title">{training.nom || `Entraînement ${tIdx + 1}`}</h3>
											)}
											<div className="training-detail-content">
												{training.exercices?.map((ex, exIdx) => {
													const prevEx = trainingDetailModal.previousTraining?.exercices?.find(
														pe => pe.idExercice === ex.idExercice
													);
													let exProgressed = 0, exTotal = 0;
													return (
														<div key={exIdx} className="training-detail-exercise">
															<div className="training-detail-exercise-header">
																<span className="training-detail-exercise-name">{ex.nom || ex.nomExercice}</span>
																{prevEx && (() => {
																	const sets = ex.series || [];
																	const prevSets = prevEx.series || [];
																	sets.forEach((s, i) => {
																		if (i < prevSets.length && s.reps > 0) {
																			exTotal++;
																			if (s.poids > prevSets[i].poids || s.reps > prevSets[i].reps) exProgressed++;
																		}
																	});
																	if (exTotal === 0) return null;
																	const pct = Math.round(exProgressed / exTotal * 100);
																	const color = pct >= 60 ? "#4ade80" : pct >= 40 ? "#facc15" : pct >= 20 ? "#F87B1B" : "#ef4444";
																	return (
																		<span className="training-detail-exercise-prog" style={{ color }}>
																			{pct}% ({exProgressed}/{exTotal})
																		</span>
																	);
																})()}
																{!prevEx && <span className="training-detail-exercise-prog" style={{ color: "rgba(255,255,255,0.3)" }}>—</span>}
															</div>
															<div className="training-detail-sets">
																{(ex.series || []).map((s, sIdx) => {
																	const prevSet = prevEx?.series?.[sIdx];
																	const comparable = prevSet && s.reps > 0;
																	const improved = comparable && (s.poids > prevSet.poids || s.reps > prevSet.reps);
																	const regressed = comparable && !improved && (s.poids < prevSet.poids || s.reps < prevSet.reps);
																	return (
																		<div key={sIdx} className={`training-detail-set ${improved ? "improved" : ""} ${regressed ? "regressed" : ""}`}>
																			<span className="training-detail-set-num">S{sIdx + 1}</span>
																			<span className="training-detail-set-value">{s.poids}kg × {s.reps}</span>
																			{s.rpe > 0 && <span className="training-detail-set-rpe">RPE {s.rpe}</span>}
																			{prevSet && (
																				<span className="training-detail-set-prev">
																					(préc: {prevSet.poids}kg × {prevSet.reps})
																				</span>
																			)}
																		</div>
																	);
																})}
															</div>
														</div>
													);
												})}
												{training.comEntrainement && (
													<div className="training-detail-comment">
														<strong>Notes :</strong> {training.comEntrainement}
													</div>
												)}
											</div>
										</div>
									))}
								</div>
							) : (
								<div className="stats-empty">Entraînement introuvable</div>
							)}
						</div>
					</div>
				</div>
			)}

			{/* Template Name Modal */}
			{showTemplateNameModal && (
				<div className="modal-overlay" onClick={closeTemplateNameModal}>
					<div className="modal-content template-name-modal" onClick={(e) => e.stopPropagation()}>
						<button className="modal-close" onClick={closeTemplateNameModal}>&times;</button>
						<div className="modal-inner">
							<h2 className="modal-title">Sauvegarder comme template</h2>
							<div className="modal-divider"></div>
							{!isSelf && (
								<div className="template-save-target">
									<button className={`template-list-tab ${templateSaveTarget === "student" ? "active" : ""}`} onClick={() => setTemplateSaveTarget("student")}>Template de {selectedStudent?.prenom}</button>
									<button className={`template-list-tab ${templateSaveTarget === "coach" ? "active" : ""}`} onClick={() => setTemplateSaveTarget("coach")}>Mon template</button>
								</div>
							)}
							<div className="template-name-input-container">
								<input
									type="text"
									className="template-name-input"
									placeholder="Entrez le nom du template..."
									value={templateName}
									onChange={(e) => setTemplateName(e.target.value)}
									autoFocus
								/>
							</div>
							<button
								className="modal-btn"
								onClick={handleSaveAsTemplate}
								disabled={!templateName.trim()}
							>
								Enregistrer
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Create Exercise Modal */}
			{showCreateExerciseModal && (
				<div className="modal-overlay" onClick={closeCreateExerciseModal}>
					<div className="modal-content" onClick={(e) => e.stopPropagation()}>
						<button className="modal-close" onClick={closeCreateExerciseModal}>&times;</button>
						<div className="modal-inner">
							<h2 className="modal-title">Nouvel exercice</h2>
							<div className="modal-divider"></div>
							<div className="exercise-edit-form">
								<div className="exercise-edit-field">
									<label className="exercise-edit-label">Nom</label>
									<input
										type="text"
										className="exercise-edit-input"
										value={newExerciseName}
										onChange={(e) => setNewExerciseName(e.target.value)}
										placeholder="Nom de l'exercice"
										autoFocus
									/>
								</div>
								<div className="exercise-edit-field">
									<label className="exercise-edit-label">Description</label>
									<textarea
										className="exercise-edit-textarea"
										value={newExerciseDescription}
										onChange={(e) => setNewExerciseDescription(e.target.value)}
										placeholder="Description de l'exercice"
										rows={4}
									/>
								</div>
								<div className="exercise-edit-field">
									<label className="exercise-edit-label">Muscles</label>
									<div className="exercise-edit-muscles">
										{MUSCLE_LABELS.map((muscle, index) => (
											<div
												key={index}
												className={`exercise-edit-muscle-chip ${newExerciseMuscles[index] === 1 ? 'selected' : ''}`}
												onClick={() => toggleNewExerciseMuscle(index)}
											>
												{muscle}
											</div>
										))}
									</div>
								</div>
							</div>
							<div className="exercise-edit-buttons">
								<button
									className="modal-btn modal-btn-secondary"
									onClick={closeCreateExerciseModal}
									disabled={creatingExercise}
								>
									Annuler
								</button>
								<button
									className="modal-btn"
									onClick={handleCreateExercise}
									disabled={creatingExercise || !newExerciseName.trim()}
								>
									{creatingExercise ? "Création..." : "Créer"}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Training Comment Modal */}
			{showCommentModal && (
				<div className="modal-overlay" onClick={closeCommentModal}>
					<div className="modal-content" onClick={(e) => e.stopPropagation()}>
						<button className="modal-close" onClick={closeCommentModal}>&times;</button>
						<div className="modal-inner">
							<h2 className="modal-title">Note de la séance</h2>
							<div className="modal-divider"></div>
							<div className="exercise-edit-form">
								<div className="exercise-edit-field">
									<textarea
										className="exercise-edit-textarea comment-textarea"
										value={editingComment}
										onChange={(e) => setEditingComment(e.target.value)}
										placeholder="Ajoutez une note pour cette séance..."
										rows={6}
										autoFocus
									/>
								</div>
							</div>
							<div className="exercise-edit-buttons">
								<button
									className="modal-btn modal-btn-secondary"
									onClick={closeCommentModal}
								>
									Annuler
								</button>
								<button
									className="modal-btn"
									onClick={saveComment}
								>
									Enregistrer
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

export default CoachMode;
